import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getDashboardDetails,
  getDashboardPayment,
  getAndroidDownloads,
} from "../../../../Service/getDashboard";
import { DashboardImages, CommonImages } from "../../../../reusableUI/images";
import Role from "../../../../Charts/roleGraph";
import Card from "../../../../reusableUI/CommonCardUi";
import Loader from "../../../../reusableUI/loader";
import UserCities from "../../../../Charts/userCities";

const Dashboard = () => {
  const navigate = useNavigate();
  const [dashList, setDashList] = useState({
    activeUsers: 0,
    totalUsers: 0,
    totalUsersAllTime: 0,
    bookings: 0,
    registrations: { newRegistration: 0, registrations: [] },
    downloads: [],
    pendingActions: { unassignedVisits: 0, pendingVerifications: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [nurseList, setNurseList] = useState([]);
  const [patientList, setPatientList] = useState([]);
  const [physiotherapist, setPhysiotherapist] = useState([]);
  const [percentageRound, setPercentage] = useState("0");
  const [iosData, setIosData] = useState([]);
  const [androidData, setAndroidData] = useState([]);
  const [androidDownloads, setAndroidDownloads] = useState(0);
  const [loadingAndroidDownloads, setLoadingAndroidDownloads] = useState(true);
  const [revenue, setRevenue] = useState(0);
  const [loadingRevenue, setLoadingRevenue] = useState(true);
  const [timePeriod, setTimePeriod] = useState("alltime"); // Time period state

  // Click handlers for pending actions navigation - using React Router for instant navigation
  const handleUnassignedVisitsClick = () => {
    navigate("/booking");
  };

  const handlePendingVerificationsClick = () => {
    navigate("/user", {
      state: {
        verification: "pending",
        fromDashboard: true,
      },
    });
  };

  useEffect(() => {
    getRMList();
    getPayment();
    fetchAndroidDownloads();
  }, [timePeriod]); // Re-fetch when time period changes

  const fetchAndroidDownloads = async () => {
    try {
      setLoadingAndroidDownloads(true);
      const result = await getAndroidDownloads(timePeriod);
      if (result && !result.error && result.data) {
        setAndroidDownloads(result.data.android || 0);
        console.log(
          `Android downloads for ${timePeriod}:`,
          result.data.android
        );
      } else {
        console.error("Error fetching Android downloads");
        setAndroidDownloads(0);
      }
    } catch (error) {
      console.error("Error fetching Android downloads:", error);
      setAndroidDownloads(0);
    } finally {
      setLoadingAndroidDownloads(false);
    }
  };

  const getPayment = async () => {
    try {
      setLoadingRevenue(true);
      const result = await getDashboardPayment(timePeriod);
      console.log(result.data);
      if (result?.data) {
        setRevenue(result.data.total_collected);
      } else {
        console.error("No payment data available");
      }
    } catch (error) {
      console.error("Error fetching payment data:", error);
    } finally {
      setLoadingRevenue(false);
    }
  };
  console.log(revenue);
  const getRMList = async () => {
    try {
      setLoading(true);
      const result = await getDashboardDetails(timePeriod);
      if (result && !result.error && result.data) {
        let iosList =
          result?.data?.downloads?.filter((item) => item.platform === "ios") ||
          [];
        let androidList =
          result?.data?.downloads?.filter(
            (item) => item.platform === "android"
          ) || [];
        let nurseList =
          result?.data?.registrations?.registrations?.filter(
            (item) => item.role === "nurse"
          ) || [];
        let patientList =
          result?.data?.registrations?.registrations?.filter(
            (item) => item.role === "patient"
          ) || [];
        let physiotherapistList =
          result?.data?.registrations?.registrations?.filter(
            (item) => item.role === "physiotherapist"
          ) || [];
        setDashList(result.data);
        setIosData(iosList);
        setAndroidData(androidList);
        setNurseList(nurseList);
        setPatientList(patientList);
        setPhysiotherapist(physiotherapistList);
      } else {
        console.error("No dashboard data available");
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  let activeUsers = dashList?.activeUsers || 0;
  let totalUsers = dashList?.totalUsers || 0;
  let totalUsersAllTime = dashList?.totalUsersAllTime || 0;
  let percentage =
    totalUsersAllTime > 0 ? (activeUsers / totalUsersAllTime) * 100 : 0;
  // Ensure percentage is a valid number between 0-100
  let round = Number(percentage.toFixed(0));
  // Clamp between 0-100 to prevent any invalid values
  if (isNaN(round) || round < 0) round = 0;
  if (round > 100) round = 100;

  // Helper function to get period label
  const getPeriodLabel = () => {
    switch (timePeriod) {
      case "today":
        return "Today";
      case "last7days":
        return "Last 7 Days";
      case "last30days":
        return "Last 30 Days";
      case "alltime":
        return "All Time";
      default:
        return "Today";
    }
  };

  // Helper function to get revenue title
  const getRevenueTitle = () => {
    switch (timePeriod) {
      case "today":
        return "Revenue Generated Today";
      case "last7days":
        return "Revenue (Last 7 Days)";
      case "last30days":
        return "Revenue (Last 30 Days)";
      case "alltime":
        return "Total Revenue Collected";
      default:
        return "Revenue Generated";
    }
  };

  // Helper function to get active users lookback period text
  const getActiveUsersText = () => {
    switch (timePeriod) {
      case "today":
        return `${activeUsers} active users out of ${totalUsersAllTime} users (active today)`;
      case "last7days":
        return `${activeUsers} active users out of ${totalUsersAllTime} users (active in last 3 days)`;
      case "last30days":
        return `${activeUsers} active users out of ${totalUsersAllTime} users (active in last 15 days)`;
      case "alltime":
        return `${activeUsers} active users out of ${totalUsersAllTime} users (active in last 3 months)`;
      default:
        return `${activeUsers} active users out of ${totalUsersAllTime} users`;
    }
  };

  return (
    <div>
      {loading ? (
        <div className="">
          <Loader loading={loading} />
        </div>
      ) : (
        <div className="overflow-y-auto bg-[#F3F6F5] w-full h-[90vh] overflow-x-hidden mb-20">
          {/* Header with Time Period Selector */}
          <div className="flex flex-row items-center justify-between px-4 py-4 bg-white shadow-sm mb-2 mx-2 rounded-md">
            <div className="flex flex-row items-center gap-3">
              <h2 className="text-xl font-bold text-gray-800">Dashboard</h2>
              <span className="relative flex items-center">
                <span className="absolute inline-flex h-2.5 w-2.5 animate-ping rounded-full bg-green-500 opacity-75"></span>
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500"></span>
              </span>
              <span className="text-sm text-gray-600 font-medium">
                {getPeriodLabel()}
              </span>
            </div>

            {/* Time Period Selector */}
            <div className="flex gap-2">
              <button
                onClick={() => setTimePeriod("today")}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${timePeriod === "today"
                    ? "bg-[#4951EC] text-white shadow-md shadow-blue-200"
                    : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
                  }`}
              >
                Today
              </button>
              <button
                onClick={() => setTimePeriod("last7days")}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${timePeriod === "last7days"
                    ? "bg-[#4951EC] text-white shadow-md shadow-blue-200"
                    : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
                  }`}
              >
                Last 7 Days
              </button>
              <button
                onClick={() => setTimePeriod("last30days")}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${timePeriod === "last30days"
                    ? "bg-[#4951EC] text-white shadow-md shadow-blue-200"
                    : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
                  }`}
              >
                Last 30 Days
              </button>
              <button
                onClick={() => setTimePeriod("alltime")}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${timePeriod === "alltime"
                    ? "bg-[#4951EC] text-white shadow-md shadow-blue-200"
                    : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
                  }`}
              >
                All Time
              </button>
            </div>
          </div>

          {/* Metric Cards */}
          <div className="flex flex-nowrap justify-center items-stretch w-full gap-1">
            <Card
              title={"Active Users"}
              image={CommonImages.active}
              progress={round}
              text={getActiveUsersText()}
              id="1"
              user={"dash1"}
            />
            <Card
              title={"Bookings"}
              image={DashboardImages.booking}
              image1={CommonImages.Up}
              count={dashList.bookings}
              text={`${dashList?.bookings
                } bookings in ${getPeriodLabel().toLowerCase()}`}
              id="2"
              user={"dash"}
            />
            <Card
              title={"New Registrations"}
              image={CommonImages.userReg}
              image1={CommonImages.Up}
              count={dashList?.registrations?.newRegistration}
              text={`${dashList?.registrations?.newRegistration
                } new registrations in ${getPeriodLabel().toLowerCase()}`}
              id={"3"}
              user={"dash"}
            />
            {/* Conditional rendering of the revenue card based on the loading state */}
            {loadingRevenue ? (
              <Card
                title={getRevenueTitle()}
                image={DashboardImages.revenue}
                image1={CommonImages.Up}
                count={"Loading..."}
                text={"Calculating..."}
                id={"4"}
                user={"dash"}
              />
            ) : (
              <Card
                title={getRevenueTitle()}
                image={DashboardImages.revenue}
                image1={CommonImages.Up}
                count={"₹" + `${revenue}`}
                text={`From ${getPeriodLabel().toLowerCase()}`}
                id={"4"}
                user={"dash"}
              />
            )}
          </div>

          {/* Charts Section - User by Role and User by Cities (Time Period Filtered) */}
          <div className="px-2">
            {/* Common Legend */}
            <div className="px-5 py-3 mb-0">
              <div className="flex flex-wrap justify-center gap-4">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-sm"
                    style={{ backgroundColor: "#A4E472" }}
                  ></div>
                  <span className="text-xs font-medium text-gray-700">
                    Nurse
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-sm"
                    style={{ backgroundColor: "#A3C8F4" }}
                  ></div>
                  <span className="text-xs font-medium text-gray-700">
                    Patient
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-sm"
                    style={{ backgroundColor: "#9DAEF4" }}
                  ></div>
                  <span className="text-xs font-medium text-gray-700">
                    Physiotherapist
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
              <div>
                <Role
                  registrations={{ nurseList, patientList, physiotherapist }}
                  hideLegend={true}
                  timePeriod={timePeriod}
                />
              </div>

              <div>
                <UserCities
                  users={dashList?.registrations?.registrations}
                  hideLegend={true}
                />
              </div>
            </div>

            {/* Pending Actions Card - All Time (Not Filtered) */}
            <div className="bg-white rounded-[12px] shadow-md mb-8 overflow-hidden border border-gray-200">
              <div className="bg-white px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-[16px] font-bold text-gray-800">
                    Pending Actions
                  </h3>
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                    ALL TIME
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-0 bg-white">
                {/* Unassigned Visits - Clickable Card */}
                <div
                  onClick={handleUnassignedVisitsClick}
                  className="group relative p-8 cursor-pointer transition-all duration-200 hover:bg-red-50 border-r border-gray-200 hover:shadow-md bg-white"
                  style={{ minHeight: "180px" }}
                >
                  <div className="flex items-center justify-between h-full">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="bg-red-100 rounded-xl p-4 group-hover:bg-red-200 transition-colors duration-300">
                          <svg
                            className="w-10 h-10 text-red-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="text-5xl font-bold text-red-600 group-hover:text-red-700 transition-colors">
                            {dashList?.pendingActions?.unassignedVisits || 0}
                          </p>
                        </div>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-gray-800 mb-1">
                          Unassigned Visits
                        </p>
                        <p className="text-sm text-gray-600">
                          Patients waiting for healthcare providers
                        </p>
                      </div>
                    </div>
                    <div className="ml-4">
                      <svg
                        className="w-8 h-8 text-gray-400 group-hover:text-red-600 group-hover:translate-x-1 transition-all duration-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </div>
                  {/* Hover indicator */}
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-red-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                </div>

                {/* Pending Verifications - Clickable Card */}
                <div
                  onClick={handlePendingVerificationsClick}
                  className="group relative p-8 cursor-pointer transition-all duration-200 hover:bg-yellow-50 hover:shadow-md bg-white"
                  style={{ minHeight: "180px" }}
                >
                  <div className="flex items-center justify-between h-full">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="bg-yellow-100 rounded-xl p-4 group-hover:bg-yellow-200 transition-colors duration-300">
                          <svg
                            className="w-10 h-10 text-yellow-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="text-5xl font-bold text-yellow-600 group-hover:text-yellow-700 transition-colors">
                            {dashList?.pendingActions?.pendingVerifications ||
                              0}
                          </p>
                        </div>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-gray-800 mb-1">
                          Pending Verifications
                        </p>
                        <p className="text-sm text-gray-600">
                          Documents awaiting review
                        </p>
                      </div>
                    </div>
                    <div className="ml-4">
                      <svg
                        className="w-8 h-8 text-gray-400 group-hover:text-yellow-600 group-hover:translate-x-1 transition-all duration-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </div>
                  {/* Hover indicator */}
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-yellow-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
